import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import type { TeamMemberTasks } from '../../lib/dashboardApi';
import { TaskListRow } from '../tasks/TaskListRow';
import { Avatar } from './Avatar';

interface TeamTasksPanelProps {
  members: TeamMemberTasks[];
}

function MemberCard({ member }: { member: TeamMemberTasks }) {
  return (
    <div className="min-w-0 rounded-xl border border-border/60 bg-muted/20 p-3 sm:p-4">
      <div className="flex items-center gap-3">
        <Link to={`/people/${member.person.id}`} className="shrink-0">
          <Avatar name={member.person.name} id={member.person.id} size={40} />
        </Link>
        <div className="min-w-0 flex-1">
          <Link
            to={`/people/${member.person.id}`}
            className="block truncate text-sm font-semibold text-foreground hover:underline"
          >
            {member.person.name}
          </Link>
          {member.person.title && (
            <p className="truncate text-xs text-muted-foreground">{member.person.title}</p>
          )}
        </div>
        {/* Stacked on a phone: two chips side by side plus the name did not
            fit in the ~300px a nested card has there. */}
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-1.5">
          {member.inProgressCount > 0 && (
            <span className="whitespace-nowrap rounded-full bg-blue-500/20 px-2 py-0.5 text-[11px] font-medium text-blue-300">
              {member.inProgressCount} în lucru
            </span>
          )}
          {member.overdueCount > 0 && (
            <span className="whitespace-nowrap rounded-full bg-red-500/20 px-2 py-0.5 text-[11px] font-medium text-red-300">
              {member.overdueCount} întârziat{member.overdueCount === 1 ? '' : 'e'}
            </span>
          )}
        </div>
      </div>

      {/* Same row as "Task-uri & urgențe"; the card header already names the
          person, so the trailing avatar is the only redundant bit. */}
      <ul className="mt-3 overflow-hidden rounded-md border border-border/60">
        {member.tasks.slice(0, 5).map((t) => (
          <TaskListRow key={t.id} task={t} tone={t.daysOverdue > 0 ? 'overdue' : 'plain'} />
        ))}
      </ul>
      {member.tasks.length > 5 && (
        <Link
          to={`/people/${member.person.id}`}
          className="mt-2 inline-block text-xs text-blue-400 hover:underline"
        >
          +{member.tasks.length - 5} mai multe →
        </Link>
      )}
    </div>
  );
}

/**
 * Real team tasks grouped by person (CLE-257). Each person card lists their
 * open tasks (from the built-in ticketing system) with project badges, priority,
 * status and deadline. Clicking a task opens its detail; clicking a person opens
 * their people-detail page.
 */
export function TeamTasksPanel({ members }: TeamTasksPanelProps) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-6">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-purple-400" aria-hidden />
        <h3 className="text-sm font-semibold text-foreground">Task-uri echipă · pe persoană</h3>
      </div>

      {members.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground/70">
          Niciun task activ alocat echipei momentan.
        </p>
      ) : (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {members.map((m) => (
            <MemberCard key={m.person.id} member={m} />
          ))}
        </div>
      )}
    </section>
  );
}
